import moment from "moment";
import {
	playerSeasonSummaryCards,
	technicalGroups,
	ratioLabels,
} from "@/utils/computePlayerStats";
import {
	computeAllOveralls,
	computePlaystyles,
	computeRadarAreas,
	ROLE_LABELS,
} from "@/config/smartValuationConfig";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch {
	jsPDF = null;
}

// ── Theme ─────────────────────────────────────────────────────────────────────

const theme = {
	primary: [155, 27, 42],
	primaryDark: [107, 15, 26],
	primaryLight: [196, 39, 59],
	bg: [245, 247, 250],
	border: [229, 231, 235],
	text: [30, 41, 59],
	muted: [100, 116, 139],
	white: [255, 255, 255],
	success: [22, 163, 74],
	warn: [217, 119, 6],
	danger: [220, 38, 38],
};

// ── Spacing ───────────────────────────────────────────────────────────────────

const SP = {
	pagePad: 42,
	blockGap: 12,
	cardPad: 12,
	footerH: 44,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const safeText = (v, fallback = "—") => {
	if (v === null || v === undefined || v === "") return fallback;
	return String(v);
};

const ROLE_SHORT = {
	Portiere: "POR",
	Difensore: "DIF",
	Mediano: "MED",
	Centrocampista: "CEN",
	Trequartista: "TRE",
	Attaccante: "ATT",
};

// ── Main export ───────────────────────────────────────────────────────────────

export const generatePlayerReportPDF = ({
	playerData,
	contracts,
	injuries,
	valuations,
	realStats,
	smartValues,
}) => {
	if (!jsPDF) throw new Error("jsPDF non disponibile");
	if (!playerData) throw new Error("Dati giocatore mancanti");

	const doc = new jsPDF({orientation: "portrait", unit: "pt", format: "a4"});
	const pageW = doc.internal.pageSize.getWidth();
	const pageH = doc.internal.pageSize.getHeight();
	const contentW = pageW - SP.pagePad * 2;
	let y = 0;

	// ── Utility ───────────────────────────────────────────────────────────────

	const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);
	const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);
	const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);

	const checkPageBreak = (needed) => {
		if (y + needed > pageH - SP.footerH) {
			doc.addPage();
			y = SP.pagePad;
			return true;
		}
		return false;
	};

	const drawCard = (cx, cy, cw, ch, opts = {}) => {
		const r = opts.radius ?? 6;
		if (opts.shadow) {
			setFill([0, 0, 0]);
			doc.setGState(new doc.GState({opacity: 0.04}));
			doc.roundedRect(cx + 1, cy + 1, cw, ch, r, r, "F");
			doc.setGState(new doc.GState({opacity: 1}));
		}
		setFill(opts.fill ?? theme.white);
		setDraw(opts.border ?? theme.border);
		doc.setLineWidth(0.5);
		doc.roundedRect(cx, cy, cw, ch, r, r, "FD");
	};

	const drawSectionTitle = (title) => {
		checkPageBreak(30);
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(10);
		setColor(theme.primary);
		doc.text(title.toUpperCase(), SP.pagePad, y);
		y += 4;
		setDraw(theme.primary);
		doc.setLineWidth(1.5);
		doc.line(SP.pagePad, y, SP.pagePad + 40, y);
		y += SP.blockGap;
	};

	const fmtVal = (v, d = 2) => {
		if (v == null || isNaN(v)) return "—";
		return Number(v).toFixed(d);
	};

	// ── Page 1: Header ────────────────────────────────────────────────────────

	const headerH = 88;
	setFill(theme.primaryDark);
	doc.rect(0, 0, pageW, headerH, "F");

	// Accent line
	setFill(theme.primary);
	doc.rect(0, headerH - 3, pageW, 3, "F");

	// Title
	doc.setFont("Helvetica", "bold");
	doc.setFontSize(22);
	setColor(theme.white);
	const playerName =
		`${playerData.firstName || ""} ${playerData.lastName || ""}`.trim();
	doc.text(playerName.toUpperCase(), SP.pagePad, 38);

	// Subtitle
	doc.setFont("Helvetica", "normal");
	doc.setFontSize(11);
	doc.setTextColor(200, 200, 210);
	const roleShort = playerData.goalkeeper
		? "POR"
		: ROLE_SHORT[playerData.primaryRole] || "";
	const age = playerData.birthDate
		? moment().diff(moment(playerData.birthDate), "years")
		: null;
	const subtitleParts = [
		roleShort,
		playerData.primaryRole,
		age ? `${age} anni` : null,
		playerData.birthDate
			? moment(playerData.birthDate).format("DD/MM/YYYY")
			: null,
	].filter(Boolean);
	doc.text(subtitleParts.join("  ·  "), SP.pagePad, 56);

	// Report label
	doc.setFont("Helvetica", "bold");
	doc.setFontSize(9);
	doc.setTextColor(180, 180, 190);
	doc.text("INTELLIGENCE REPORT", SP.pagePad, 74);
	doc.setFont("Helvetica", "normal");
	doc.text(
		moment().format("DD/MM/YYYY"),
		pageW - SP.pagePad - doc.getTextWidth(moment().format("DD/MM/YYYY")),
		74,
	);

	y = headerH + SP.blockGap + 8;

	// ── Profilo & Contratto ───────────────────────────────────────────────────

	drawSectionTitle("Profilo");

	const profileFields = [
		{
			label: "Categoria",
			value: safeText(playerData.category || playerData.playersCategory),
		},
		{label: "Status", value: safeText(playerData.status)},
		{label: "Piede", value: safeText(playerData.preferredFoot)},
		{label: "Nazionalita", value: safeText(playerData.nationality)},
		{
			label: "Altezza",
			value: playerData.height ? `${playerData.height} cm` : "—",
		},
		{label: "Peso", value: playerData.weight ? `${playerData.weight} kg` : "—"},
	];

	const colW = contentW / 3;
	const cardH = 42;
	drawCard(SP.pagePad, y, contentW, cardH * 2 + SP.cardPad * 2 + 4, {
		shadow: true,
	});

	profileFields.forEach((f, i) => {
		const col = i % 3;
		const row = Math.floor(i / 3);
		const cx = SP.pagePad + SP.cardPad + col * colW;
		const cy = y + SP.cardPad + row * cardH;

		doc.setFont("Helvetica", "normal");
		doc.setFontSize(8);
		setColor(theme.muted);
		doc.text(f.label.toUpperCase(), cx, cy + 10);

		doc.setFont("Helvetica", "bold");
		doc.setFontSize(11);
		setColor(theme.text);
		doc.text(f.value, cx, cy + 24);
	});

	y += cardH * 2 + SP.cardPad * 2 + 4 + SP.blockGap;

	// ── Contratto ─────────────────────────────────────────────────────────────

	const activeContract = (contracts || [])
		.filter((c) => c.endDate && moment(c.endDate).isAfter(moment()))
		.sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];

	drawSectionTitle("Contratto");

	const contractCardH = 48;
	drawCard(SP.pagePad, y, contentW, contractCardH, {shadow: true});

	if (activeContract) {
		const daysLeft = moment(activeContract.endDate).diff(moment(), "days");
		const contractColor =
			daysLeft > 365
				? theme.success
				: daysLeft > 180
					? theme.warn
					: theme.danger;

		const cFields = [
			{
				label: "INIZIO",
				value: moment(activeContract.startDate).format("DD/MM/YYYY"),
			},
			{
				label: "SCADENZA",
				value: moment(activeContract.endDate).format("DD/MM/YYYY"),
			},
			{
				label: "GIORNI RIMANENTI",
				value: String(daysLeft),
				color: contractColor,
			},
			{
				label: "TIPO",
				value: safeText(activeContract.contractType || playerData.status),
			},
		];

		const cColW = contentW / 4;
		cFields.forEach((f, i) => {
			const cx = SP.pagePad + SP.cardPad + i * cColW;
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			doc.text(f.label, cx, y + 16);

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(12);
			setColor(f.color || theme.text);
			doc.text(f.value, cx, y + 32);
		});
	} else {
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(10);
		setColor(theme.muted);
		doc.text("Nessun contratto attivo", SP.pagePad + SP.cardPad, y + 28);
	}

	y += contractCardH + SP.blockGap;

	// Dati della stessa stagione e delle stesse rose autorizzate del profilo.
	const profile = realStats?.outfield?.profile;
	checkPageBreak(90);
	drawSectionTitle(
		profile
			? `Partite - stagione ${profile.seasonName}`
			: "Statistiche delle partite",
	);
	const note = profile
		? `${profile.pastMatches} gare trascorse. Totali sui dati rilevati; — indica un dato mancante o in conflitto.`
		: realStats?.error || "Statistiche delle partite non disponibili.";
	doc.setFont("Helvetica", "normal");
	doc.setFontSize(8);
	setColor(theme.muted);
	const noteLines = doc.splitTextToSize(note, contentW);
	doc.text(noteLines, SP.pagePad, y + 8);
	y += noteLines.length * 10 + SP.blockGap;
	const cards = playerSeasonSummaryCards(realStats);
	for (let start = 0; start < cards.length; start += 4) {
		checkPageBreak(64);
		for (const [i, card] of cards.slice(start, start + 4).entries()) {
			const width = (contentW - 18) / 4;
			const x = SP.pagePad + i * (width + 6);
			drawCard(x, y, width, 54, {fill: theme.bg});
			doc.setFont("Helvetica", "bold");
			doc.setFontSize(14);
			setColor(theme.text);
			doc.text(card.value, x + 8, y + 18);
			doc.setFontSize(7);
			setColor(theme.muted);
			doc.text(card.label.toUpperCase(), x + 8, y + 31);
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(6);
			doc.text(doc.splitTextToSize(card.coverage, width - 16), x + 8, y + 42);
		}
		y += 64;
	}
	const drawTableHeader = () => {
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(7);
		setColor(theme.muted);
		doc.text("METRICA", SP.pagePad + 4, y + 8);
		doc.text("TOTALE", SP.pagePad + 275, y + 8);
		doc.text("PER 90", SP.pagePad + 340, y + 8);
		doc.text("GARE RILEVATE", SP.pagePad + 415, y + 8);
		y += 18;
	};
	if (profile) {
		for (const group of technicalGroups.filter(
			(g) => !g.goalkeeper || profile.goalkeeper,
		)) {
			const fields = group.fields.filter(
				([key]) => profile.totals[key]?.value != null,
			);
			if (!fields.length) continue;
			checkPageBreak(72);
			drawSectionTitle(group.title);

			drawTableHeader();
			for (const [key, label] of fields) {
				if (checkPageBreak(24)) {
					drawSectionTitle(group.title + " (continua)");
					drawTableHeader();
				}
				const total = profile.totals[key];
				doc.setFont("Helvetica", "normal");
				doc.setFontSize(8);
				setColor(theme.text);
				doc.text(label, SP.pagePad + 4, y + 9);
				doc.text(
					fmtVal(total.value, Number.isInteger(total.value) ? 0 : 2),
					SP.pagePad + 275,
					y + 9,
				);
				doc.text(fmtVal(total.per90), SP.pagePad + 340, y + 9);
				doc.text(
					`${total.knownMatches}/${total.eligibleMatches}`,
					SP.pagePad + 415,
					y + 9,
				);
				y += 20;
			}
			y += SP.blockGap;
		}
		const ratios = Object.entries(ratioLabels).filter(
			([key]) => profile.ratios[key]?.percentage != null,
		);
		if (ratios.length) {
			checkPageBreak(64);
			drawSectionTitle("Percentuali su dati abbinati");
			for (const [key, label] of ratios) {
				if (checkPageBreak(24)) drawSectionTitle("Percentuali (continua)");
				const ratio = profile.ratios[key];
				doc.setFont("Helvetica", "normal");
				doc.setFontSize(8);
				setColor(theme.text);
				doc.text(label, SP.pagePad + 4, y + 9);
				doc.text(
					`${fmtVal(ratio.percentage, 1)}% (${ratio.successful}/${ratio.attempted}) - ${ratio.knownMatches} gare`,
					SP.pagePad + 275,
					y + 9,
				);
				y += 20;
			}
		}
		checkPageBreak(40);
		doc.setFontSize(7);
		setColor(theme.muted);
		doc.text(
			"Per 90 e percentuali usano solo gare con dati abbinati noti. Nessuna somma con fonti esterne.",
			SP.pagePad,
			y + 9,
		);
		y += 24;
	}

	// ── Storico infortuni ─────────────────────────────────────────────────────

	if (injuries && injuries.length > 0) {
		checkPageBreak(80);
		drawSectionTitle("Storico Infortuni");

		const injCardH = 26;
		const totalH = injuries.length * injCardH + SP.cardPad * 2;
		drawCard(SP.pagePad, y, contentW, totalH, {shadow: true});

		injuries.forEach((inj, i) => {
			const iy = y + SP.cardPad + i * injCardH;
			const isActive =
				!inj.sportsDischargeDate ||
				moment(inj.sportsDischargeDate).isAfter(moment());

			// Zebra
			if (i % 2 === 0) {
				setFill(theme.bg);
				doc.rect(SP.pagePad + 1, iy, contentW - 2, injCardH, "F");
			}

			// Status dot
			setFill(isActive ? theme.danger : theme.success);
			doc.circle(SP.pagePad + SP.cardPad + 4, iy + injCardH / 2, 3, "F");

			// Tipo/diagnosi
			doc.setFont("Helvetica", "bold");
			doc.setFontSize(9);
			setColor(theme.text);
			const diagnosis = safeText(
				inj.diagnosis || inj.injuryType || inj.type,
				"Infortunio",
			);
			doc.text(diagnosis, SP.pagePad + SP.cardPad + 14, iy + 10);

			// Parte corpo
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			const bodyPart = safeText(inj.bodyPart || inj.area, "");
			if (bodyPart !== "—") {
				doc.text(bodyPart, SP.pagePad + SP.cardPad + 14, iy + 21);
			}

			// Date
			doc.setFont("Helvetica", "normal");
			doc.setFontSize(8);
			setColor(theme.muted);
			const startDate = inj.injuryDate || inj.startDate;
			const endDate = inj.sportsDischargeDate;
			const dateStr = startDate
				? moment(startDate).format("DD/MM/YYYY") +
					(endDate
						? ` → ${moment(endDate).format("DD/MM/YYYY")}`
						: " → in corso")
				: "";
			const dateW = doc.getTextWidth(dateStr);
			doc.text(dateStr, pageW - SP.pagePad - SP.cardPad - dateW, iy + 10);

			// Giorni persi
			if (startDate) {
				const end = endDate ? moment(endDate) : moment();
				const daysLost = end.diff(moment(startDate), "days");
				const daysStr = `${daysLost} giorni`;
				doc.setFont("Helvetica", "bold");
				doc.setFontSize(8);
				setColor(isActive ? theme.danger : theme.muted);
				const daysW = doc.getTextWidth(daysStr);
				doc.text(daysStr, pageW - SP.pagePad - SP.cardPad - daysW, iy + 21);
			}
		});

		y += totalH + SP.blockGap;
	}

	// ── Valutazioni ───────────────────────────────────────────────────────────

	if (valuations && valuations.length > 0) {
		checkPageBreak(80);
		drawSectionTitle("Valutazioni");

		const sorted = [...valuations].sort(
			(a, b) =>
				new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt),
		);

		const valRowH = 24;
		const headerRowH = 18;
		const totalValH = headerRowH + sorted.length * valRowH + SP.cardPad * 2;
		drawCard(SP.pagePad, y, contentW, totalValH, {shadow: true});

		// Header
		const vy = y + SP.cardPad;
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(7.5);
		setColor(theme.muted);
		doc.text("DATA", SP.pagePad + SP.cardPad, vy + 10);
		doc.text("AUTORE", SP.pagePad + SP.cardPad + 80, vy + 10);
		doc.text("RATING", pageW - SP.pagePad - SP.cardPad - 30, vy + 10);

		setDraw(theme.border);
		doc.setLineWidth(0.5);
		doc.line(
			SP.pagePad + SP.cardPad,
			vy + headerRowH,
			pageW - SP.pagePad - SP.cardPad,
			vy + headerRowH,
		);

		sorted.forEach((val, i) => {
			const ry = vy + headerRowH + i * valRowH;

			if (i % 2 === 0) {
				setFill(theme.bg);
				doc.rect(SP.pagePad + 1, ry, contentW - 2, valRowH, "F");
			}

			doc.setFont("Helvetica", "normal");
			doc.setFontSize(9);
			setColor(theme.text);
			const valDate = val.date || val.createdAt;
			doc.text(
				valDate ? moment(valDate).format("DD/MM/YYYY") : "—",
				SP.pagePad + SP.cardPad,
				ry + 15,
			);

			doc.text(
				safeText(val.author || val.scout || val.evaluator),
				SP.pagePad + SP.cardPad + 80,
				ry + 15,
			);

			// Rating
			const rating = val.rating || val.totalRating || val.averageRating;
			if (rating != null) {
				doc.setFont("Helvetica", "bold");
				doc.setFontSize(11);
				const rc =
					rating >= 7 ? theme.success : rating >= 5 ? theme.warn : theme.danger;
				setColor(rc);
				const ratingStr = Number(rating).toFixed(1);
				const rw = doc.getTextWidth(ratingStr);
				doc.text(ratingStr, pageW - SP.pagePad - SP.cardPad - rw, ry + 16);
			}
		});

		y += totalValH + SP.blockGap;
	}

	// ── Profilo SMART ─────────────────────────────────────────────────────────

	if (smartValues && Object.values(smartValues).some((v) => v != null)) {
		checkPageBreak(120);
		drawSectionTitle("Profilo SMART");

		const overalls = computeAllOveralls(smartValues);
		const playstyles = computePlaystyles(smartValues);
		const radarAreas = computeRadarAreas(smartValues);

		// Overall per ruolo
		const overallCardH = 44;
		drawCard(SP.pagePad, y, contentW, overallCardH, {shadow: true});

		const roles = ["ATT", "CC", "DIF"];
		const roleColW = contentW / 3;
		const bestRole = roles.reduce((a, b) =>
			overalls[a] >= overalls[b] ? a : b,
		);

		roles.forEach((role, i) => {
			const cx = SP.pagePad + i * roleColW + roleColW / 2;
			const isBest = role === bestRole;

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(isBest ? 20 : 16);
			setColor(isBest ? theme.primary : theme.text);
			const valStr = String(overalls[role]);
			const vw = doc.getTextWidth(valStr);
			doc.text(valStr, cx - vw / 2, y + 18);

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(8);
			setColor(theme.muted);
			const label = ROLE_LABELS[role];
			const lw = doc.getTextWidth(label);
			doc.text(label, cx - lw / 2, y + 32);

			if (i < 2) {
				setDraw(theme.border);
				doc.setLineWidth(0.5);
				doc.line(
					SP.pagePad + (i + 1) * roleColW,
					y + 6,
					SP.pagePad + (i + 1) * roleColW,
					y + overallCardH - 6,
				);
			}
		});

		y += overallCardH + SP.blockGap;

		// Radar areas (barre)
		if (radarAreas.length) {
			const barH = 18;
			const radarCardH = radarAreas.length * barH + SP.cardPad * 2;
			checkPageBreak(radarCardH + 10);
			drawCard(SP.pagePad, y, contentW, radarCardH, {shadow: true});

			radarAreas.forEach((area, i) => {
				const ry = y + SP.cardPad + i * barH;

				doc.setFont("Helvetica", "bold");
				doc.setFontSize(9);
				setColor(theme.text);
				doc.text(area.area, SP.pagePad + SP.cardPad, ry + 12);

				// Barra
				const barX = SP.pagePad + SP.cardPad + 70;
				const barW = contentW - SP.cardPad * 2 - 70 - 30;
				setFill(theme.bg);
				doc.roundedRect(barX, ry + 4, barW, 8, 4, 4, "F");

				const fillW = (area.value / 100) * barW;
				const barColor =
					area.value >= 65
						? theme.success
						: area.value >= 40
							? theme.warn
							: theme.danger;
				setFill(barColor);
				doc.roundedRect(barX, ry + 4, Math.max(fillW, 4), 8, 4, 4, "F");

				// Valore
				doc.setFont("Helvetica", "bold");
				doc.setFontSize(9);
				setColor(theme.text);
				doc.text(String(area.value), barX + barW + 6, ry + 12);
			});

			y += radarCardH + SP.blockGap;
		}

		// Top 3 playstyle
		if (playstyles.length) {
			const top3 = playstyles.slice(0, 3);
			const psCardH = top3.length * 20 + SP.cardPad * 2;
			checkPageBreak(psCardH + 10);

			doc.setFont("Helvetica", "bold");
			doc.setFontSize(9);
			setColor(theme.primaryDark);
			doc.text("PLAYSTYLE", SP.pagePad, y + 2);
			y += 14;

			drawCard(SP.pagePad, y, contentW, psCardH, {shadow: true});

			top3.forEach((ps, i) => {
				const ry = y + SP.cardPad + i * 20;

				doc.setFont("Helvetica", "bold");
				doc.setFontSize(10);
				setColor(i === 0 ? theme.primary : theme.text);
				doc.text(`${ps.emoji} ${ps.label}`, SP.pagePad + SP.cardPad, ry + 13);

				doc.setFont("Helvetica", "bold");
				doc.setFontSize(10);
				const scoreStr = String(ps.score);
				const sw = doc.getTextWidth(scoreStr);
				doc.text(scoreStr, pageW - SP.pagePad - SP.cardPad - sw, ry + 13);
			});

			y += psCardH + SP.blockGap;
		}
	}

	// ── Raccomandazione ───────────────────────────────────────────────────────

	{
		checkPageBreak(80);
		drawSectionTitle("Raccomandazione");

		// Calcolo algoritmico della raccomandazione
		const age = playerData.birthDate
			? moment().diff(moment(playerData.birthDate), "years")
			: null;

		const daysLeft = activeContract?.endDate
			? moment(activeContract.endDate).diff(moment(), "days")
			: null;

		const totalInjuryDays = (injuries || []).reduce((sum, inj) => {
			const start = inj.startDate || inj.injuryDate;
			if (!start) return sum;
			const end = inj.sportsDischargeDate
				? moment(inj.sportsDischargeDate)
				: moment();
			return sum + Math.max(0, moment(end).diff(moment(start), "days"));
		}, 0);

		const matchesPlayed = realStats?.outfield?.matchesPlayed ?? null;
		const latestRating = valuations?.length
			? (() => {
					const ratings = valuations
						.map((v) =>
							parseFloat(v.finalOverallRating || v.averageRating || v.rating),
						)
						.filter((v) => !isNaN(v));
					return ratings.length ? ratings[ratings.length - 1] : null;
				})()
			: null;

		// Punteggio raccomandazione (0-100)
		let recScore = 50;
		const recFactors = [];

		// Eta (giovani: bonus, over 30: malus — contesto youth)
		if (age != null) {
			if (age <= 17) {
				recScore += 10;
				recFactors.push("Eta formativa");
			} else if (age <= 20) {
				recScore += 5;
				recFactors.push("Eta di sviluppo");
			} else if (age >= 28) {
				recScore -= 10;
				recFactors.push("Eta avanzata per settore giovanile");
			}
		}

		// Contratto
		if (daysLeft != null) {
			if (daysLeft <= 90) {
				recScore -= 15;
				recFactors.push("Contratto in scadenza imminente");
			} else if (daysLeft <= 180) {
				recScore -= 5;
				recFactors.push("Contratto da monitorare");
			} else {
				recScore += 5;
				recFactors.push("Contratto sicuro");
			}
		} else {
			recScore -= 10;
			recFactors.push("Nessun contratto attivo");
		}

		// Infortuni
		if (totalInjuryDays > 90) {
			recScore -= 15;
			recFactors.push("Frequenti infortuni (" + totalInjuryDays + "gg)");
		} else if (totalInjuryDays > 30) {
			recScore -= 5;
			recFactors.push("Alcuni infortuni (" + totalInjuryDays + "gg)");
		} else {
			recScore += 5;
			recFactors.push("Buona salute");
		}

		if (matchesPlayed != null && realStats.outfield.matchesAvailable > 0) {
			recFactors.push(
				`Presenze registrate: ${matchesPlayed}/${realStats.outfield.matchesAvailable} gare`,
			);
		}

		// Rating
		if (latestRating != null) {
			if (latestRating >= 7) {
				recScore += 10;
				recFactors.push("Rating elevato (" + latestRating.toFixed(1) + ")");
			} else if (latestRating >= 5.5) {
				recFactors.push("Rating nella media (" + latestRating.toFixed(1) + ")");
			} else {
				recScore -= 10;
				recFactors.push("Rating basso (" + latestRating.toFixed(1) + ")");
			}
		}

		recScore = Math.min(100, Math.max(0, recScore));

		const recLabel =
			recScore >= 70
				? "RINNOVARE"
				: recScore >= 40
					? "MONITORARE"
					: "CEDERE/NON RINNOVARE";
		const recColor =
			recScore >= 70
				? theme.success
				: recScore >= 40
					? theme.warn
					: theme.danger;

		// Render
		const recCardH = 70 + recFactors.length * 12;
		drawCard(SP.pagePad, y, contentW, recCardH, {shadow: true});

		// Label grande
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(18);
		setColor(recColor);
		doc.text(recLabel, SP.pagePad + SP.cardPad, y + 24);

		// Score
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(10);
		setColor(theme.muted);
		const scoreStr = `Score: ${recScore}/100`;
		const scoreW = doc.getTextWidth(scoreStr);
		doc.text(scoreStr, pageW - SP.pagePad - SP.cardPad - scoreW, y + 24);

		// Divider
		setDraw(theme.border);
		doc.setLineWidth(0.5);
		doc.line(
			SP.pagePad + SP.cardPad,
			y + 34,
			pageW - SP.pagePad - SP.cardPad,
			y + 34,
		);

		// Fattori
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(8.5);
		setColor(theme.text);
		recFactors.forEach((factor, i) => {
			doc.text(`• ${factor}`, SP.pagePad + SP.cardPad, y + 48 + i * 12);
		});

		y += recCardH + SP.blockGap;
	}

	// ── Footer su tutte le pagine ─────────────────────────────────────────────

	const totalPages = doc.internal.getNumberOfPages();
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p);

		// Divider
		setDraw(theme.border);
		doc.setLineWidth(0.5);
		doc.line(
			SP.pagePad,
			pageH - SP.footerH + 10,
			pageW - SP.pagePad,
			pageH - SP.footerH + 10,
		);

		// Date
		doc.setFont("Helvetica", "normal");
		doc.setFontSize(8);
		setColor(theme.muted);
		doc.text(
			`Generato il ${moment().format("DD/MM/YYYY HH:mm")}`,
			SP.pagePad,
			pageH - 20,
		);

		// Page
		const pageStr = `Pagina ${p} / ${totalPages}`;
		const pageStrW = doc.getTextWidth(pageStr);
		doc.text(pageStr, pageW - SP.pagePad - pageStrW, pageH - 20);

		// Branding
		doc.setFont("Helvetica", "bold");
		doc.setFontSize(7);
		setColor(theme.primary);
		const brand = "FOOTBALL DECISION OS";
		const brandW = doc.getTextWidth(brand);
		doc.text(brand, pageW / 2 - brandW / 2, pageH - 20);
	}

	// ── Save ──────────────────────────────────────────────────────────────────

	const sanitize = (s) => (s || "").replace(/[^a-zA-Z0-9]/g, "_");
	const fileName = `Report_${sanitize(playerData.lastName)}_${sanitize(playerData.firstName)}_${moment().format("YYYY-MM-DD")}.pdf`;
	doc.save(fileName);
};
